import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const OrgContext = createContext(null);
const COMPANY_KEY = "diamond_company"; // "" / absent = All Businesses (group view)

export function OrgProvider({ children }) {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [current, setCurrent] = useState(null); // {org, role, permissions, entitlements, companies, is_platform_owner}
  const [companies, setCompanies] = useState([]);
  const [activeCompanyId, setActiveCompanyId] = useState(localStorage.getItem(COMPANY_KEY) || null);
  const [loading, setLoading] = useState(true);

  const loadCurrent = useCallback(async () => {
    try {
      const { data } = await api.get("/orgs/current");
      setCurrent(data);
      setCompanies(data.companies || []);
      localStorage.setItem("diamond_org", data.org.id);
      // drop a stale company selection that doesn't belong to this org
      const saved = localStorage.getItem(COMPANY_KEY);
      if (saved && !(data.companies || []).some((c) => c.id === saved)) {
        localStorage.removeItem(COMPANY_KEY);
        setActiveCompanyId(null);
      }
    } catch (e) {
      setCurrent(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadOrgs = useCallback(async () => {
    try {
      const { data } = await api.get("/orgs");
      setOrgs(data);
      const saved = localStorage.getItem("diamond_org");
      if (!saved && data[0]) localStorage.setItem("diamond_org", data[0].id);
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (user) {
      loadOrgs();
      loadCurrent();
    } else {
      setLoading(false);
    }
  }, [user, loadOrgs, loadCurrent]);

  const switchOrg = async (orgId) => {
    localStorage.setItem("diamond_org", orgId);
    localStorage.removeItem(COMPANY_KEY); // reset to All Businesses when changing group
    setActiveCompanyId(null);
    setLoading(true);
    await loadCurrent();
  };

  // companyId === null => All Businesses (group-wide view)
  const switchCompany = (companyId) => {
    if (companyId) localStorage.setItem(COMPANY_KEY, companyId);
    else localStorage.removeItem(COMPANY_KEY);
    setActiveCompanyId(companyId || null);
  };

  const refreshCompanies = useCallback(async () => {
    try {
      const { data } = await api.get("/companies");
      setCompanies(data);
      return data;
    } catch (e) { return []; }
  }, []);

  const can = (perm) => {
    const p = current?.permissions || [];
    return p.includes("*") || p.includes(perm);
  };
  const hasFeature = (f) => {
    const feats = current?.entitlements?.features || [];
    return feats.includes("*") || feats.includes(f);
  };
  const openPortal = async () => {
    const { data } = await api.post("/billing/portal");
    if (data.portal_url) window.location.href = data.portal_url;
  };

  const activeCompany = companies.find((c) => c.id === activeCompanyId) || null;
  const isGroupView = !activeCompanyId;

  return (
    <OrgContext.Provider value={{
      orgs, current, loading, companies, activeCompanyId, activeCompany, isGroupView,
      switchOrg, switchCompany, refreshCompanies,
      refresh: loadCurrent, refreshOrgs: loadOrgs, can, hasFeature,
      subscription: current?.subscription, openPortal,
    }}>
      {children}
    </OrgContext.Provider>
  );
}

export const useOrg = () => useContext(OrgContext);
